import { useState, useEffect } from 'react';
import { Users, UserPlus, Check, Clock, X, Search, ChevronRight } from 'lucide-react';
import { useSocial } from '../hooks/useSocial';
import * as SocialAPI from '../services/SocialAPI';
import FriendDetailView from './FriendDetailView';

const initials = (username) => (username ? username.slice(0, 2).toUpperCase() : '?');

const Avatar = ({ username }) => (
  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-yellow to-brick flex items-center justify-center text-navy-dark text-sm font-heading font-bold flex-shrink-0">
    {initials(username)}
  </div>
);

const FriendsView = () => {
  const {
    friends,
    incoming,
    outgoing,
    friendsLoaded,
    sendFriendRequest,
    acceptFriendRequest,
    removeFriendship
  } = useSocial();

  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null); // null = no search active
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState('');
  const [confirmUnfriend, setConfirmUnfriend] = useState(null); // friendshipId
  const [viewingFriend, setViewingFriend] = useState(null); // friend object → detail drill-down

  // Debounced user search
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults(null);
      return;
    }

    const timeoutId = setTimeout(async () => {
      try {
        setSearching(true);
        setResults(await SocialAPI.searchUsers(q));
      } catch (err) {
        console.error('User search error:', err);
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [query]);

  const handleSend = async (username) => {
    setError('');
    try {
      await sendFriendRequest(username);
      // Refresh relationship tags on visible results
      if (query.trim().length >= 2) {
        setResults(await SocialAPI.searchUsers(query.trim()));
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAccept = async (friendshipId) => {
    setError('');
    try {
      await acceptFriendRequest(friendshipId);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRemove = async (friendshipId) => {
    setError('');
    setConfirmUnfriend(null);
    try {
      await removeFriendship(friendshipId);
    } catch (err) {
      setError(err.message);
    }
  };

  if (viewingFriend) {
    return <FriendDetailView friend={viewingFriend} onBack={() => setViewingFriend(null)} />;
  }

  if (!friendsLoaded) {
    return (
      <div className="flex flex-col items-center justify-center py-24">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-navy-dark border-t-transparent mb-4"></div>
        <span className="text-ink-secondary text-sm">Loading friends...</span>
      </div>
    );
  }

  return (
    <div className="px-4 md:px-10 py-6 max-w-3xl mx-auto">
      <h2 className="font-heading font-bold text-xl text-ink-primary mb-5">Friends</h2>

      {/* Search */}
      <div className="relative mb-6">
        <Search size={16} className="absolute left-3.5 top-3.5 text-ink-faint" />
        <input
          type="text"
          placeholder="Find collectors by username..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-3 bg-white border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-yellow"
        />
        {searching && (
          <div className="absolute right-3.5 top-3.5">
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-navy-dark border-t-transparent"></div>
          </div>
        )}
      </div>

      {error && (
        <div className="mb-4 px-3 py-2.5 bg-danger-bg border border-danger-border rounded-xl text-brick text-[13px]">
          {error}
        </div>
      )}

      {/* Search results */}
      {results !== null && (
        <div className="mb-6">
          <h3 className="text-[12px] font-semibold uppercase tracking-wide text-ink-muted mb-2">
            Search results
          </h3>
          {results.length === 0 ? (
            <p className="text-[13px] text-ink-secondary">
              {searching ? 'Searching...' : `No collectors found matching "${query.trim()}"`}
            </p>
          ) : (
            <div className="space-y-2">
              {results.map((u) => (
                <div key={u.id} className="bg-white border border-border rounded-xl p-3 flex items-center gap-3">
                  <Avatar username={u.username} />
                  <span className="flex-1 text-sm font-semibold text-ink-primary truncate">{u.username}</span>
                  {u.relationship === 'self' && (
                    <span className="text-[12px] text-ink-muted">That's you</span>
                  )}
                  {u.relationship === 'friends' && (
                    <span className="text-[12px] font-medium text-success-green flex items-center gap-1">
                      <Check size={13} /> Friends
                    </span>
                  )}
                  {u.relationship === 'outgoing' && (
                    <span className="text-[12px] font-medium text-ink-secondary flex items-center gap-1">
                      <Clock size={13} /> Pending
                    </span>
                  )}
                  {u.relationship === 'incoming' && (
                    <button
                      onClick={() => handleAccept(u.friendshipId)}
                      className="bg-success-bg text-success-green border border-success-border text-[12px] font-semibold px-3 py-1.5 rounded-lg hover:bg-success-border transition-colors"
                    >
                      Accept
                    </button>
                  )}
                  {u.relationship === 'none' && (
                    <button
                      onClick={() => handleSend(u.username)}
                      className="bg-navy-dark text-white text-[12px] font-semibold px-3 py-1.5 rounded-lg hover:bg-navy-light transition-colors flex items-center gap-1.5"
                    >
                      <UserPlus size={13} /> Add
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Incoming requests */}
      {incoming.length > 0 && (
        <div className="mb-6">
          <h3 className="text-[12px] font-semibold uppercase tracking-wide text-ink-muted mb-2">
            Friend requests
          </h3>
          <div className="space-y-2">
            {incoming.map((r) => (
              <div key={r.friendshipId} className="bg-white border border-yellow/50 rounded-xl p-3 flex items-center gap-3">
                <Avatar username={r.username} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-ink-primary truncate">{r.username}</div>
                  <div className="text-[11px] text-ink-secondary">wants to be friends</div>
                </div>
                <button
                  onClick={() => handleAccept(r.friendshipId)}
                  className="bg-success-bg text-success-green border border-success-border text-[12px] font-semibold px-3 py-1.5 rounded-lg hover:bg-success-border transition-colors"
                >
                  Accept
                </button>
                <button
                  onClick={() => handleRemove(r.friendshipId)}
                  title="Decline"
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-ink-faint hover:text-brick hover:bg-danger-bg transition-colors"
                >
                  <X size={15} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Outgoing requests */}
      {outgoing.length > 0 && (
        <div className="mb-6">
          <h3 className="text-[12px] font-semibold uppercase tracking-wide text-ink-muted mb-2">
            Sent requests
          </h3>
          <div className="space-y-2">
            {outgoing.map((r) => (
              <div key={r.friendshipId} className="bg-white border border-border rounded-xl p-3 flex items-center gap-3">
                <Avatar username={r.username} />
                <span className="flex-1 text-sm font-semibold text-ink-primary truncate">{r.username}</span>
                <span className="text-[12px] text-ink-secondary flex items-center gap-1">
                  <Clock size={13} /> Pending
                </span>
                <button
                  onClick={() => handleRemove(r.friendshipId)}
                  title="Cancel request"
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-ink-faint hover:text-brick hover:bg-danger-bg transition-colors"
                >
                  <X size={15} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Friends list */}
      <div>
        <h3 className="text-[12px] font-semibold uppercase tracking-wide text-ink-muted mb-2">
          Your friends ({friends.length})
        </h3>
        {friends.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center bg-white border border-border rounded-2xl">
            <div className="w-12 h-12 rounded-full bg-cream-bg border border-border flex items-center justify-center mb-3">
              <Users size={20} className="text-ink-faint" />
            </div>
            <p className="text-[13px] text-ink-secondary max-w-xs">
              No friends yet. Search for collectors by username to send a request.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {friends.map((f) => (
              <div key={f.friendshipId} className="bg-white border border-border rounded-xl p-3 flex items-center gap-3">
                <button
                  onClick={() => setViewingFriend(f)}
                  className="flex items-center gap-3 flex-1 min-w-0 text-left"
                >
                  <Avatar username={f.username} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-ink-primary truncate flex items-center gap-1">
                      {f.username}
                      <ChevronRight size={14} className="text-ink-faint" />
                    </div>
                    {f.since && (
                      <div className="text-[11px] text-ink-secondary">
                        Friends since {new Date(f.since).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                </button>
                {confirmUnfriend === f.friendshipId ? (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleRemove(f.friendshipId)}
                      className="bg-danger-bg text-brick border border-danger-border text-[12px] font-semibold px-3 py-1.5 rounded-lg hover:bg-danger-border transition-colors"
                    >
                      Unfriend
                    </button>
                    <button
                      onClick={() => setConfirmUnfriend(null)}
                      className="text-[12px] text-ink-secondary px-2 py-1.5"
                    >
                      Keep
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setConfirmUnfriend(f.friendshipId)}
                    className="text-[12px] text-ink-faint hover:text-brick px-2 py-1.5 transition-colors"
                  >
                    Unfriend
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FriendsView;
